import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { IconButton } from '@material-ui/core';
  import {
    Receipt as ReceiptIcon,
    MoreHoriz as MoreHorizIcon,
    CheckCircle as CheckCircleIcon,
    CancelOutlined as CancelOutlinedIcon,
    Refresh as RefreshIcon,
    RotateLeft as RotateLeftIcon,
    Delete as DeleteIcon,
    Search as PageviewIcon,
  } from '@material-ui/icons';

import { isAlternativeLoadingStatus } from  '../../../../utils/store/store-utils';
import SimpleConsultPageTemplate from "../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate";

import { changeToCreateMode,selectMasters,selectStatus,resetModel,resetMode,fetchAllAsync, selectMasterType, selectOperationMaster } from '../../../../features/master/masterSlice';
import { userHasDeleteRole } from '../../../../components/perfil/perfil-utils';
import { getTitleOfMaster } from '../../../../features/master/masterUtils';

export default function MasterConsultPage({  authInfo = {}  } ){

    const { t } = useTranslation();
    const theme = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();

    const status = useSelector(selectStatus);
    const masters = useSelector(selectMasters);
    const type = useSelector(selectMasterType);
    const operation = useSelector(selectOperationMaster)

    const isAlternativeLoading = useMemo(() => isAlternativeLoadingStatus(status), [status]);

    const resetModes = useCallback(() => {
      dispatch(resetMode());
    }, []);
  
    const fetchAll = useCallback(() => {
      dispatch(fetchAllAsync([
        { name: 'page', value: 1 },
        { name: 'size', value: 10 },
        { name: 'numero', value: '' },
      ]));
    }, []);
  
    const resetModels = useCallback(() => {
      dispatch(resetModel());
    }, []);
  
    const handleActivateInactivate = useCallback((event, id) => {

    },[]);

    const handleClickOnEditButton = useCallback( (event, id) => {
      event.stopPropagation();
      history.push(t('com.muralis.qcx.url.moduloAgenciamentoMasterConsultar', { id }));
    },[history])

    useEffect( () => {
      fetchAll();
      resetModes();
      resetModels();
    }, []);

    const columns = useMemo(
        () => [
          {
            field: 'master',
            headerName: t('com.muralis.qcx.master.table.master'),
            headerAlign: 'center',
            align: 'center',
            type: 'string',
            flex: 160,
            valueGetter: ({ row }) => row?.numeroMaster || '-',
          },
          {
            field: 'consignatario',
            headerName: t('com.muralis.qcx.master.table.consignatario'),
            headerAlign: 'center',
            align: 'center',
            type: 'string',
            flex: 160,
            valueGetter: ({ row }) => row?.consignatario.description
            || '-',
          },
          {
            field: 'cia_aerea',
            headerName: t('com.muralis.qcx.master.table.cia_aerea'),
            headerAlign: 'center',
            align: 'center',
            type: 'string',
            flex: 120,
            valueGetter: ({ row }) => row?.transportadorInternacional?.description || '-',
          },
          {
            field: 'origem',
            headerName: t('com.muralis.qcx.master.table.origem'),
            headerAlign: 'center',
            align: 'center',
            type: 'string',
            flex: 140,
            valueGetter: ({ row }) => row?.origem?.description || '-',
          },
          {
            field: 'destino',
            headerName: t('com.muralis.qcx.master.table.destino'),
            headerAlign: 'center',
            align: 'center',
            type: 'string',
            flex: 140,
            valueGetter: ({ row }) => row?.destino?.description || '-',
          },
          {
            field: 'moeda',
            headerName: t('com.muralis.qcx.master.table.moeda'),
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            flex: 150,
            valueGetter: ({ row }) => row?.masterCustos[0]?.moeda?.description,
          },
          {
            field: 'embarque',
            headerName: t('com.muralis.qcx.master.table.embarque'),
            headerAlign: 'center',
            align: 'center',
            type: 'string',
            flex: 150,
            valueGetter: ({ row }) => row?.tipoEmbarque || '-',            
          },        
          {
            field: 'actions',
            headerName: t('com.muralis.qcx.acoes.label'),
            headerAlign: 'center',
            align: 'center',
            flex: 80,
            renderCell: ({ row: { id, active } }) => (
              <>
                {userHasDeleteRole(authInfo, 'grupo-despesa-receita') && (
                <IconButton
                  key={`stw-active${id}`}
                  value={active}
                  name={`stw-active-${id}`}
                  onClick={(event) => handleActivateInactivate(event, id)}
                >
                  <DeleteIcon color="error" />
                </IconButton>
                )}
                <IconButton
                  key={`btn-view-${id}`}
                  name={`btn-view-${id}`}
                  onClick={(event) => handleClickOnEditButton(event, id)}
                >
                  <PageviewIcon color="primary" />
                </IconButton>
              </>
            ),
          },  
        ],[]
    );

    const breadcrumbs = useMemo(
        () => [
          {
            link: {
              to: '/',
              name: t('com.muralis.qcx.inicio'),
            },
          },
          {
            link: {
              to: operation === 'EXPORTACAO' ? t('com.muralis.qcx.url.moduloAgenciamentoExportacao') : t('com.muralis.qcx.url.moduloAgenciamento'),
              name: operation === 'EXPORTACAO' ? t('com.muralis.qcx.exportacao.label') : t('com.muralis.qcx.importacao.label'),
            },
          },
          {
            text: {
              name: t('com.muralis.qcx.master.title'),
            },
          },
        ],
        []
    );
    
    const handleClickOnButtonNew = useCallback( (event) => {
        event.stopPropagation();
        dispatch(changeToCreateMode());
    },[])

    const handleConfigChange = useCallback( ({ currentPage, pageSize, filterInputValue }) => {
      dispatch(
        fetchAllAsync([
          { name: 'page', value: currentPage },
          { name: 'size', value: pageSize },
          { name: 'numero', value: filterInputValue },
          { name: 'operacao', value: operation },
        ])
      );
    },[])

    const templateProps = useMemo(
    () => ({
        page: {
        title: getTitleOfMaster(type),
        icon: <ReceiptIcon />,
        breadcrumbs,
        },
        control: {
          buttonNew: {
              description: t('com.muralis.qcx.acoes.nova'),
              path: t('com.muralis.qcx.url.moduloAgenciamentoMasterNovo'),
              onClick: handleClickOnButtonNew,
          },
        },
        table: {
            ignoreControlButtonsLimit: true,
            columns,
            onConfigChangeCallback: handleConfigChange,
            rowsCount: masters?.totalElements,
        },
    }),
    [
        columns,
        breadcrumbs,
        masters,
    ]);

    return (
        <SimpleConsultPageTemplate
            pageProps={templateProps.page}
            controlProps={templateProps.control}
            tableProps={templateProps.table}
            tableData={masters?.content ?? []}
            isLoading={isAlternativeLoading}
            buttonNewText={t('com.muralis.qcx.acoes.nova').toUpperCase()}
            authInfo={authInfo}
            requiredRoles={['conciliacao-bancaria']}
        />
    )
}